import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomerQuery } from "@queries/customers";
import config from "@config/config";
import {
  GeneralTab,
  AffiliateTab,
  HistoryTab,
  BalanceTab,
  RewardPointsTab,
  CustomerTierTab,
  IpAddressesTab,
  OrderHistoryTab,
  OrderScheduleTab,
  UnpaidInvoicesTab,
  AddressesTab,
} from "@components/customers/tabs";
import "@styles/opencart-admin.css";

const ALL_TABS = [
  { key: "general", label: "General" },
  { key: "addresses", label: "Addresses" },
  { key: "history", label: "History" },
  { key: "balance", label: "Balance (Statement of Account)" },
  { key: "customer_tier", label: "Customer Tier" },
  { key: "order_history", label: "Order History" },
  { key: "customer_orders", label: "Customer Orders" },
  { key: "unpaid_invoices", label: "Unpaid Invoices" },
];

export default function CustomerEditPage() {
  const { id: routeId } = useParams();
  const isNew = !routeId;
  const id = isNew ? null : routeId;

  const nav = useNavigate();
  const { data, isLoading, isError } = useCustomerQuery(id);
  const customer = data?.data;

  const [tab, setTab] = React.useState("general");

  const initialNewCustomer = React.useMemo(
    () => ({
      customer_id: null,
      store_id: null,
      customer_group_id: null,
      customer_group: null,
      language_id: 2,
      name: "",
      email: "",
      telephone: "",
      alt_mobile: "",
      newsletter: 0,
      customer_is_tax_free: 0,
      discount_percentage: "0",
      status: 1,
      safe: 0,
      call_customer: "no",
      vip: "no",
      default_payment_method: "cod",
    }),
    []
  );

  const [draftCustomer, setDraftCustomer] = React.useState(
    isNew ? initialNewCustomer : null
  );

  const quickOrderUrl = React.useMemo(() => {
    if (!id) return "#";
    const base = `${config.apiBaseUrl}/orders/salim`;
    let editedBy = "";
    try {
      editedBy = JSON.parse(localStorage.getItem("ww_admin_auth") || "null")?.user?.username ?? "";
    } catch { /* ignore */ }
    const qs = new URLSearchParams({
      customer_id: String(id),
      name: customer?.name ?? "",
      telephone: customer?.telephone ?? "",
      store_id: customer?.store_id ?? "",
      ...(editedBy ? { edited_by: editedBy } : {}),
    });
    return `${base}?${qs.toString()}`;
  }, [id, customer]);

  React.useEffect(() => {
    if (isNew) {
      setDraftCustomer(initialNewCustomer);
    } else {
      setDraftCustomer(customer || null);
    }
  }, [customer, isNew, initialNewCustomer]);

  React.useEffect(() => {
    if (tab === "customer_orders" && id) {
      nav(`/orders?customer_id=${encodeURIComponent(id)}`);
    }
  }, [tab, id, nav]);

  const handleChangeGroup = ({ id: groupIdStr, name }) => {
    setDraftCustomer(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        customer_group_id: groupIdStr ? Number(groupIdStr) : null,
        customer_group: name || prev.customer_group,
      };
    });
  };

  const handleCreatedCustomer = React.useCallback(
    createdCustomer => {
      if (!createdCustomer) return;
      const newId = createdCustomer.customer_id || createdCustomer.id;
      if (newId) {
        nav(`/customers/${newId}`);
      }
    },
    [nav]
  );

  const tabsToRender = React.useMemo(
    () => (isNew ? ALL_TABS.filter(t => t.key === "general") : ALL_TABS),
    [isNew]
  );

  return (
    <div className="oc-content">
      <div
        className="oc-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div>
          <h1>{isNew ? "Create Customer" : "Customers"}</h1>
          <nav className="oc-bc">
            <span>Home</span>
            <span className="sep">›</span>
            <span>Customers</span>
            <span className="sep">›</span>
            <span>{isNew ? "New" : `ID:${id}`}</span>
          </nav>
          {!isNew && (
            <button
              type="button"
              onClick={() => setTab("unpaid_invoices")}
              title="Click to see all unpaid invoices"
              style={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "baseline",
                gap: 8,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#dc2626",
                fontWeight: 700,
              }}
            >
              <span style={{ fontSize: 13, textDecoration: "underline" }}>
                Total unpaid invoices:
              </span>
              <span style={{ fontSize: 20, fontWeight: 800 }}>
                {Number(customer?.total_unpaid_invoices ?? 0).toFixed(3)} JOD
              </span>
            </button>
          )}
        </div>
        {!isNew && (
          <div>
            <a
              href={quickOrderUrl}
              target="_blank"
              rel="noreferrer"
              title="Quick Order"
              className="btn btn-info"
              style={{ backgroundColor: "#72a192" }}
            >
              <i className="fa fa-exchange" />
              <span style={{ marginLeft: 6 }}>Quick Order</span>
            </a>
          </div>
        )}
      </div>

      <div className="oc-card">
        <div className="oc-tabs">
          {tabsToRender.map(t => (
            <button
              key={t.key}
              className={`oc-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ paddingTop: 12 }}>
          {!isNew && isLoading && <div>Loading…</div>}
          {!isNew && isError && (
            <div style={{ color: "#c00" }}>Error loading customer</div>
          )}

          {draftCustomer && (
            <>
              {tab === "general" && (
                <GeneralTab
                  customer={draftCustomer}
                  editable={true}
                  onChangeGroup={handleChangeGroup}
                  isNew={isNew}
                  onCreated={handleCreatedCustomer}
                />
              )}
              {!isNew && tab === "addresses" && (
                <AddressesTab customer_id={id} customer={draftCustomer} />
              )}
              {!isNew && tab === "order_history" && (
                <OrderHistoryTab customer={draftCustomer} />
              )}
              {!isNew && tab === "affiliate" && (
                <AffiliateTab customer={draftCustomer} />
              )}
              {!isNew && tab === "history" && <HistoryTab customer={draftCustomer} />}
              {!isNew && tab === "balance" && <BalanceTab customer={draftCustomer} />}
              {!isNew && tab === "reward_points" && (
                <RewardPointsTab customer={draftCustomer} />
              )}
              {!isNew && tab === "customer_tier" && (
                <CustomerTierTab customer={draftCustomer} />
              )}
              {!isNew && tab === "ip_addresses" && (
                <IpAddressesTab customer={draftCustomer} />
              )}
              {!isNew && tab === "order_schedule" && (
                <OrderScheduleTab customer={draftCustomer} />
              )}
              {!isNew && tab === "unpaid_invoices" && (
                <UnpaidInvoicesTab customer={draftCustomer} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

